/**
 * PrimarySidebarNav — the single sidebar nav list shared by every page shell
 * (AppShellV2 for Home/AI Assistant/Schedule, and the app-wide Layout for
 * everything else) so the left menu never changes shape as you navigate.
 */
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Home,
  Briefcase,
  Bot,
  Users,
  Calendar,
  Activity,
  Workflow,
  Plug,
  Server,
  ShieldCheck,
  Cpu,
  Settings,
  Moon,
  Sun,
  ChevronDown,
  ChevronRight,
  FolderKanban,
  CheckSquare,
  CalendarRange,
  RefreshCw,
  NotebookPen,
  Bot as BotIcon,
  Globe,
  Vault,
  Link2,
  Puzzle,
} from 'lucide-react';

interface NavItem {
  key: string;
  label: string;
  icon: React.ComponentType<{ size?: number }>;
  path: string;
  children?: NavItem[];
}

// "My Work" expands to the Project Management tools — restores direct
// left-menu access to Tasks / Weekly Planner / etc. that the flat 6-item
// design otherwise hid.
const MY_WORK_CHILDREN: NavItem[] = [
  { key: 'personal-projects', label: 'Personal Projects', icon: FolderKanban, path: '/tools/project-management/personal-projects' },
  { key: 'tasks', label: 'Tasks', icon: CheckSquare, path: '/tools/project-management/personal-kanban' },
  { key: 'weekly-planner', label: 'Weekly Planner', icon: CalendarRange, path: '/tools/project-management/weekly-planner' },
  { key: 'sprint-retro', label: 'Sprint Retro', icon: RefreshCw, path: '/tools/project-management/sprint-retroboard' },
  { key: 'idea-capture', label: 'Ideas & Notes', icon: NotebookPen, path: '/tools/project-management/idea-capture' },
  { key: 'project-agent', label: 'Project Agent', icon: BotIcon, path: '/tools/project-management/project-agent' },
];

export const PRIMARY_NAV: NavItem[] = [
  { key: 'home', label: 'Home', icon: Home, path: '/dashboard' },
  { key: 'my-work', label: 'My Work', icon: Briefcase, path: '/tools/project-management/personal-kanban', children: MY_WORK_CHILDREN },
  { key: 'agents', label: 'Agents', icon: Bot, path: '/agents' },
  { key: 'vault', label: 'Vault', icon: Vault, path: '/vault' },
  { key: 'teams', label: 'Teams', icon: Users, path: '/teams' },
  { key: 'schedule', label: 'Schedule', icon: Calendar, path: '/schedule' },
  { key: 'activity', label: 'Activity', icon: Activity, path: '/mission-control' },
];

export const ADVANCED_NAV: NavItem[] = [
  { key: 'community', label: 'Community', icon: Globe, path: '/marketplace' },
  { key: 'workflows', label: 'Workflows', icon: Workflow, path: '/workflows' },
  { key: 'connections', label: 'Connections', icon: Link2, path: '/connections' },
  { key: 'skills-registry', label: 'Skill registry', icon: Puzzle, path: '/skills/registry' },
  { key: 'integrations', label: 'Integrations', icon: Plug, path: '/integrations' },
  { key: 'mcp-servers', label: 'MCP Servers', icon: Server, path: '/mcp-servers' },
  { key: 'agent-harness', label: 'Agent Harness', icon: ShieldCheck, path: '/harness' },
  { key: 'models', label: 'Models', icon: Cpu, path: '/ai-console' },
];

const SETTINGS_ITEM: NavItem = { key: 'settings', label: 'Settings', icon: Settings, path: '/settings' };

interface PrimarySidebarNavProps {
  collapsed?: boolean;
  isDarkMode?: boolean;
  style?: React.CSSProperties;
  /** Only Layout.tsx passes these — AppShellV2 has no dark theme, so it omits them and no toggle renders. */
  onToggleDarkMode?: () => void;
}

export default function PrimarySidebarNav({ collapsed = false, isDarkMode = false, style, onToggleDarkMode }: PrimarySidebarNavProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const isActive = (path: string) => location.pathname === path;

  const [expanded, setExpanded] = useState<Set<string>>(() => {
    // Auto-open a group if the current page is one of its children.
    const open = new Set<string>();
    PRIMARY_NAV.forEach((item) => {
      if (item.children?.some((c) => location.pathname === c.path)) open.add(item.key);
    });
    return open;
  });

  const colors = { activeBg: 'var(--app-primary-soft)', activeText: 'var(--app-primary-deep)', text: 'var(--app-text-2)', label: 'var(--lyra-color-text-muted)' };

  const Row: React.FC<{ item: NavItem; indent?: boolean }> = ({ item, indent }) => {
    const Icon = item.icon;
    const active = isActive(item.path);
    const hasChildren = !!item.children?.length;
    const isOpen = expanded.has(item.key);
    return (
      <button
        onClick={() => {
          if (hasChildren && !collapsed) {
            setExpanded((prev) => {
              const next = new Set(prev);
              if (next.has(item.key)) next.delete(item.key); else next.add(item.key);
              return next;
            });
            return;
          }
          navigate(item.path);
        }}
        title={collapsed ? item.label : undefined}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'flex-start',
          gap: 10,
          width: '100%',
          padding: collapsed ? '9px 0' : indent ? '7px 12px 7px 30px' : '8px 12px',
          borderRadius: 10,
          border: 'none',
          background: active ? colors.activeBg : 'transparent',
          color: active ? colors.activeText : colors.text,
          fontSize: indent ? 12.5 : 13.5,
          fontWeight: active ? 600 : 500,
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <Icon size={indent ? 14 : 16} />
        {!collapsed && <span style={{ flex: 1, minWidth: 0 }}>{item.label}</span>}
        {!collapsed && hasChildren && (isOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />)}
      </button>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, ...style }}>
      <nav style={{ flex: 1, overflowY: 'auto', padding: '14px 12px', display: 'flex', flexDirection: 'column', gap: 2 }}>
        {PRIMARY_NAV.map((item) => (
          <React.Fragment key={item.key}>
            <Row item={item} />
            {!collapsed && item.children && expanded.has(item.key) && item.children.map((child) => (
              <Row key={child.key} item={child} indent />
            ))}
          </React.Fragment>
        ))}

        {!collapsed && (
          <div style={{ margin: '16px 12px 6px', fontSize: 10.5, fontWeight: 700, color: colors.label, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Advanced
          </div>
        )}
        {collapsed && <div style={{ height: 10 }} />}
        {ADVANCED_NAV.map((item) => (
          <Row key={item.key} item={item} />
        ))}
      </nav>

      <div style={{ padding: 12, borderTop: `1px solid ${'var(--lyra-color-border)'}`, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {onToggleDarkMode && (
          <button
            onClick={onToggleDarkMode}
            title={collapsed ? (isDarkMode ? 'Switch to light mode' : 'Switch to dark mode') : undefined}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: collapsed ? 'center' : 'flex-start',
              gap: 10,
              width: '100%',
              padding: collapsed ? '9px 0' : '8px 12px',
              borderRadius: 10,
              border: 'none',
              background: 'transparent',
              color: colors.text,
              fontSize: 13.5,
              fontWeight: 500,
              cursor: 'pointer',
              textAlign: 'left',
            }}
          >
            {isDarkMode ? <Sun size={16} /> : <Moon size={16} />}
            {!collapsed && <span>{isDarkMode ? 'Light mode' : 'Dark mode'}</span>}
          </button>
        )}
        <Row item={SETTINGS_ITEM} />
      </div>
    </div>
  );
}
